import Image from "next/image";
import classNames from "classnames";
import NextLink from "next/link";
import { Canvas, Typography, Link } from "@cappelendamm/design-system/react";
import { BookStencil } from "@cappelendamm/design-system/react";
import { BookItem } from "../types";
import { shouldShowAddToCartButton } from "@/products/selectors/productPurchaseSelectors";
import { getAddToCartButtonState } from "@/utils/getAddToCartButtonState";
import { ListingTags } from "@/search/components/item-listings/ListingTags";
import { getPageLink } from "@/navigation/selectors";
import { ListingItemProps } from "./types";
import { selectCollection } from "./selectors";
import { AddToCartButton } from "@/cart/components/AddToCartButton";
import { PiShoppingCartSimpleFill } from "react-icons/pi";
import { PricelistPrice } from "@/products/components/PricelistPrice";
import { MemberPrice } from "@/products/components/MemberPrice";
import { getBindingIcon } from "./getBindingIcon";

export const PRODUCT_ARTICLE_IMAGE_WIDTH = 140 as const;
export const PRODUCT_ARTICLE_IMAGE_HEIGHT = 222 as const;

export function BookListingItem({ item, config }: ListingItemProps<BookItem>) {
  const {
    objectID: sku,
    imageUrl,
    price,
    subjectToVAT,
    priceLists,
    availableBindings,
    itemDetails: { binding: format, title, language, slug, contributorByLine },
  } = item;

  const href = slug
    ? getPageLink({
        pageType: "book",
        params: slug,
      })
    : null;
  const collection = selectCollection(item);
  const bindings =
    availableBindings && availableBindings.length > 0
      ? availableBindings.map(({ binding }) => binding)
      : [format].filter((binding): binding is string => Boolean(binding));
  const addToCartState = getAddToCartButtonState(
    item.canBeSold === true,
    item.statusMessage ?? undefined,
    item.publicationDate ?? undefined,
  );
  const showAddToCart =
    (config?.fields?.addToCart ?? false) &&
    shouldShowAddToCartButton(item) &&
    !addToCartState.disabled;

  return (
    <article
      className={classNames(
        "relative row-span-[var(--list-row-count)] grid grid-rows-subgrid gap-1",
        { "cursor-pointer": href },
        href
          ? "active:rounded active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]"
          : "",
      )}
    >
      <NextLink
        href={href ?? ""}
        aria-labelledby={`item-${sku}`}
        className={classNames(
          "group relative grid grid-rows-subgrid no-underline",
          config?.fields?.series
            ? "row-span-[calc(var(--list-row-count)-1)]"
            : "row-span-full",
        )}
      >
        <Canvas className="order-1 px-2 py-2 md:px-6 md:py-2">
          <ListingTags
            item={item}
            config={{
              showHighlighted: config?.tags?.highlighted,
              showInformational: config?.tags?.informational,
              showPromoted: config?.tags?.promoted,
              showPublicationDate: config?.tags?.publicationDate,
            }}
          />
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={`Omslag av "${title}"`}
              width={PRODUCT_ARTICLE_IMAGE_WIDTH}
              height={PRODUCT_ARTICLE_IMAGE_HEIGHT}
              className="transition-all sm:group-hover:scale-102 sm:group-hover:shadow-lg"
              quality={85}
            />
          ) : (
            <BookStencil
              style={
                {
                  "--book-width": `${PRODUCT_ARTICLE_IMAGE_WIDTH}px`,
                } as React.CSSProperties
              }
            />
          )}
        </Canvas>
        <Typography
          id={`item-${sku}`}
          as="h3"
          variant="title-md"
          className={classNames("order-3 truncate", {
            "sm:group-hover:underline": href,
          })}
        >
          {title}
        </Typography>
        <Typography
          as="p"
          variant="body-base"
          className="order-4 mt-0 block truncate"
          muted
        >
          {contributorByLine}
        </Typography>
        {config?.fields?.price ? (
          <div className="order-2 flex flex-col gap-1 text-2xl font-semibold [&_span]:text-left">
            <MemberPrice
              originalItemPrice={price}
              subjectToVAT={subjectToVAT}
              pricelists={priceLists}
            >
              <PricelistPrice
                originalItemPrice={price}
                subjectToVAT={subjectToVAT}
                pricelists={priceLists}
                className="[&.flex-col]:gap-0 [&.flex-col>span.order-last]:sr-only"
              />
            </MemberPrice>
          </div>
        ) : null}
        {config?.fields?.formatMeta ? (
          <div className="order-5 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {bindings.map((binding) => {
              const Icon = getBindingIcon(binding);
              return (
                <span
                  key={binding}
                  className="inline-flex items-center gap-1 font-semibold first-letter:capitalize"
                >
                  <Icon aria-hidden size={16} />
                  {binding}
                </span>
              );
            })}
            {language ? (
              <span
                className={classNames({
                  "border-l pl-3": bindings.length > 0,
                })}
              >
                {language}
              </span>
            ) : null}
          </div>
        ) : null}
      </NextLink>
      {showAddToCart ? (
        <div className="absolute right-2 bottom-2 z-10 row-start-1 row-end-2">
          <AddToCartButton
            sku={sku}
            canBeSold={item.canBeSold}
            statusMessage={item.statusMessage ?? undefined}
            publicationDate={item.publicationDate ?? undefined}
            iconOnly
            variant="icon"
            aria-label={addToCartState.label ?? "Legg i handlekurv"}
            title={addToCartState.label ?? "Legg i handlekurv"}
          >
            <PiShoppingCartSimpleFill size={18} />
          </AddToCartButton>
        </div>
      ) : null}
      {config?.fields?.series && collection ? (
        <div className="order-6 truncate text-sm">
          {collection.prefix}
          {collection.href ? (
            <Link
              as={NextLink}
              href={collection.href}
              variant="inline"
              className={classNames({
                "inline p-0 font-semibold": collection.prefix === "En del av",
              })}
            >
              {collection.linkName}
            </Link>
          ) : (
            collection.linkName
          )}
        </div>
      ) : null}
    </article>
  );
}
